import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSpaces } from "@/context/SpaceContext";
import { VAULTS, TYPE_CONFIG, typesForVault, statusLabel } from "@/lib/config";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ItemFormDialog from "@/components/ItemFormDialog";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

const statusTone = (s) => ({
  active: "text-green-700 bg-green-50 border-green-200",
  expired: "text-red-700 bg-red-50 border-red-200",
  inactive: "text-gray-600 bg-gray-50 border-gray-200",
  pending: "text-amber-700 bg-amber-50 border-amber-200",
  archived: "text-gray-500 bg-gray-50 border-gray-200",
  new: "text-blue-700 bg-blue-50 border-blue-200",
  in_progress: "text-amber-700 bg-amber-50 border-amber-200",
  on_hold: "text-purple-700 bg-purple-50 border-purple-200",
  completed: "text-green-700 bg-green-50 border-green-200",
}[s] || "text-gray-600 bg-gray-50 border-gray-200");

export default function Vault() {
  const { vault } = useParams();
  const navigate = useNavigate();
  const { activeSpaceId, activeSpace } = useSpaces();
  const meta = VAULTS[vault];
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState("");
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragItemId, setDragItemId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const load = () => {
    if (!activeSpaceId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    api.get("/items", { params: { vault, space_id: activeSpaceId } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  const loadFolders = () => {
    if (!activeSpaceId) { setFolders([]); return Promise.resolve(); }
    return api.get("/folders", { params: { vault, space_id: activeSpaceId } }).then((r) => setFolders(r.data)).catch(() => setFolders([]));
  };
  useEffect(() => { load(); loadFolders(); setTypeFilter(""); setQ(""); setCurrentFolderId(""); /* eslint-disable-next-line */ }, [vault, activeSpaceId]);

  const foldersById = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);

  // Aggregate (folder + all descendants) counts for badges
  const totalCounts = useMemo(() => {
    const kids = {};
    folders.forEach((f) => { (kids[f.parent_id || ""] = kids[f.parent_id || ""] || []).push(f.id); });
    const memo = {};
    const calc = (id) => {
      if (memo[id] != null) return memo[id];
      let s = foldersById[id]?.count || 0;
      (kids[id] || []).forEach((c) => { s += calc(c); });
      memo[id] = s;
      return s;
    };
    folders.forEach((f) => calc(f.id));
    return memo;
  }, [folders, foldersById]);

  const breadcrumbs = useMemo(() => {
    const crumbs = [];
    let cur = currentFolderId;
    while (cur && foldersById[cur]) {
      crumbs.unshift(foldersById[cur]);
      cur = foldersById[cur].parent_id || "";
    }
    return crumbs;
  }, [currentFolderId, foldersById]);

  const subfolders = useMemo(
    () => folders.filter((f) => (f.parent_id || "") === currentFolderId).sort((a, b) => a.name.localeCompare(b.name)),
    [folders, currentFolderId]
  );

  const searching = q.trim().length > 0;

  const filtered = useMemo(() => items.filter((it) => {
    if (!searching && (it.folder_id || "") !== currentFolderId) return false;
    if (typeFilter && it.type !== typeFilter) return false;
    if (searching) {
      const hay = `${it.title} ${it.department} ${it.category} ${(it.tags || []).join(" ")}`.toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  }), [items, currentFolderId, typeFilter, q, searching]);

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/folders", { vault, name, parent_id: currentFolderId, space_id: activeSpaceId });
      setNewFolderName(""); setNewFolderOpen(false);
      await loadFolders();
      toast.success(`Folder "${data.name}" created`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create folder"); }
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"?\nAny sub-folders are removed and all items inside move to Unfiled.`)) return;
    try {
      await api.delete(`/folders/${f.id}`);
      if (currentFolderId === f.id) setCurrentFolderId(f.parent_id || "");
      await Promise.all([loadFolders(), api.get("/items", { params: { vault, space_id: activeSpaceId } }).then((r) => setItems(r.data))]);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete folder"); }
  };

  const moveItem = async (itemId, folderId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it || (it.folder_id || "") === (folderId || "")) return;
    // optimistic
    setItems((prev) => prev.map((x) => (x.id === itemId ? { ...x, folder_id: folderId || "" } : x)));
    try {
      await api.post(`/items/${itemId}/move`, { folder_id: folderId || "" });
      const dest = folderId ? foldersById[folderId]?.name : "Unfiled";
      toast.success(`Moved to ${dest}`);
      loadFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move item");
      load();
    }
  };

  const onItemDragStart = (e, id) => { setDragItemId(id); e.dataTransfer.effectAllowed = "move"; };
  const onFolderDrop = (e, folderId) => {
    e.preventDefault();
    setDropTarget(null);
    if (dragItemId) moveItem(dragItemId, folderId);
    setDragItemId(null);
  };
  const dropProps = (targetKey, folderId) => ({
    onDragOver: (e) => { if (dragItemId) { e.preventDefault(); setDropTarget(targetKey); } },
    onDragLeave: () => setDropTarget((t) => (t === targetKey ? null : t)),
    onDrop: (e) => onFolderDrop(e, folderId),
  });

  const types = typesForVault(vault);

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: `${meta.color}15` }}>
            <Icon name={meta.icon} className="w-6 h-6" style={{ color: meta.color }} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">{meta.label}</h1>
            <p className="text-sm text-gray-500">{activeSpace ? <>In <span className="font-medium text-gray-700">{activeSpace.name}</span> · {meta.tagline}</> : meta.tagline}</p>
          </div>
        </div>
        <Button className="bg-[#0A0A0A] hover:bg-gray-800" data-testid="add-asset-btn" onClick={() => setDialogOpen(true)}>
          <Icons.Plus className="w-4 h-4 mr-1.5" /> New asset
        </Button>
      </header>

      {/* Breadcrumbs */}
      <nav className="flex flex-wrap items-center gap-1 text-sm mb-3" data-testid="folder-breadcrumbs">
        <button data-testid="folder-home"
          onClick={() => setCurrentFolderId("")}
          {...dropProps("home", "")}
          className={`px-2.5 py-1.5 rounded-md font-medium flex items-center gap-1.5 transition-colors ${currentFolderId === "" ? "text-[#1B4332]" : "text-gray-500 hover:text-gray-800"} ${dropTarget === "home" ? "ring-2 ring-[#1B4332] bg-[#1B4332]/10" : ""}`}>
          <Icons.Home className="w-4 h-4" /> {meta.label}
        </button>
        {breadcrumbs.map((c) => (
          <React.Fragment key={c.id}>
            <Icons.ChevronRight className="w-4 h-4 text-gray-300" />
            <button data-testid={`crumb-${c.id}`}
              onClick={() => setCurrentFolderId(c.id)}
              {...dropProps(`crumb-${c.id}`, c.id)}
              className={`px-2.5 py-1.5 rounded-md font-medium transition-colors ${c.id === currentFolderId ? "text-[#1B4332]" : "text-gray-500 hover:text-gray-800"} ${dropTarget === `crumb-${c.id}` ? "ring-2 ring-[#1B4332] bg-[#1B4332]/10" : ""}`}>
              {c.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {/* Sub-folders */}
      {!searching && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="folder-bar">
          {subfolders.map((f) => (
            <div key={f.id} data-testid={`folder-${f.id}`}
              onClick={() => setCurrentFolderId(f.id)}
              {...dropProps(`folder-${f.id}`, f.id)}
              className={`group cursor-pointer px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-all bg-white ${dropTarget === `folder-${f.id}` ? "ring-2 ring-[#1B4332] border-[#1B4332] scale-[1.03]" : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}`}>
              <Icons.Folder className="w-4 h-4 text-[#1B4332]" />
              <span className="text-gray-700">{f.name}</span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-500">{totalCounts[f.id] || 0}</Badge>
              <Icons.X data-testid={`delete-folder-${f.id}`}
                onClick={(e) => { e.stopPropagation(); deleteFolder(f); }}
                className="w-3.5 h-3.5 text-gray-300 hover:text-red-500 md:opacity-40 md:group-hover:opacity-100 transition-opacity" />
            </div>
          ))}

          {newFolderOpen ? (
            <div className="flex items-center gap-1.5">
              <Input autoFocus value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") { setNewFolderOpen(false); setNewFolderName(""); } }}
                placeholder="Folder name" className="h-9 w-40" data-testid="new-folder-input" />
              <Button size="sm" onClick={createFolder} className="bg-[#1B4332] hover:bg-[#14472F] h-9" data-testid="new-folder-save">Add</Button>
              <Button size="sm" variant="ghost" className="h-9" onClick={() => { setNewFolderOpen(false); setNewFolderName(""); }}>Cancel</Button>
            </div>
          ) : (
            <button onClick={() => setNewFolderOpen(true)} data-testid="new-folder-btn"
              className="px-3 py-2 rounded-lg text-sm font-medium border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 flex items-center gap-1.5 transition-colors">
              <Icons.FolderPlus className="w-4 h-4" /> New folder
            </button>
          )}
        </div>
      )}

      {/* Search + type filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Icons.Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all assets in this vault…" className="pl-9" data-testid="vault-search" />
        </div>
        <button onClick={() => setTypeFilter("")} data-testid="filter-all"
          className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${!typeFilter ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          All
        </button>
        {types.map((t) => (
          <button key={t.key} onClick={() => setTypeFilter(t.key)} data-testid={`filter-${t.key}`}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5 ${typeFilter === t.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            <Icon name={t.icon} className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {searching && (
        <p className="text-xs text-gray-400 mb-2" data-testid="search-hint">Showing search results across all folders. Clear search to browse folders.</p>
      )}
      {!searching && dragItemId && (
        <p className="text-xs text-[#1B4332] mb-2 flex items-center gap-1.5" data-testid="drag-hint">
          <Icons.Move className="w-3.5 h-3.5" /> Drop on a folder or breadcrumb to move it.
        </p>
      )}

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_140px_140px_120px_110px] gap-4 px-5 py-3 border-b border-gray-100 text-[11px] uppercase tracking-wider font-semibold text-gray-400">
          <div>Asset</div><div>Department</div><div>Owner</div><div>Expires</div><div>Status</div>
        </div>
        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <Icon name={meta.icon} className="w-8 h-8 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">{searching ? "No matching assets." : currentFolderId ? "This folder has no assets yet." : "No assets yet in this vault."}</p>
            {!searching && <Button variant="outline" className="mt-4" onClick={() => setDialogOpen(true)}>Add an asset here</Button>}
          </div>
        ) : (
          filtered.map((it) => {
            const cfg = TYPE_CONFIG[it.type] || {};
            return (
              <button key={it.id} data-testid={`item-row-${it.id}`}
                draggable={!searching}
                onDragStart={(e) => onItemDragStart(e, it.id)}
                onDragEnd={() => { setDragItemId(null); setDropTarget(null); }}
                onClick={() => navigate(`/item/${it.id}`)}
                className={`w-full flex items-center justify-between gap-4 px-5 py-3.5 border-b border-gray-50 md:grid md:grid-cols-[1fr_140px_140px_120px_110px] md:items-center text-left hover:bg-gray-50 transition-colors ${dragItemId === it.id ? "opacity-40" : ""} ${!searching ? "cursor-grab active:cursor-grabbing" : ""}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 shrink-0 rounded-md bg-gray-100 flex items-center justify-center">
                    <Icon name={cfg.icon || "Circle"} className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-medium truncate ${it.vault === "todos" && it.status === "completed" ? "line-through text-gray-400" : ""}`}>{it.title}</div>
                    <div className="text-xs text-gray-400 capitalize truncate">{cfg.label || it.type}{it.department ? ` · ${it.department}` : ""}{it.category ? ` · ${it.category}` : ""}</div>
                  </div>
                </div>
                <div className="hidden md:block text-sm text-gray-600 truncate">{it.department || "—"}</div>
                <div className="hidden md:block text-sm text-gray-600 truncate">{it.owner || "—"}</div>
                <div className="hidden md:block text-sm text-gray-600">{it.expiration_date ? new Date(it.expiration_date).toLocaleDateString() : "—"}</div>
                <div className="shrink-0"><Badge variant="outline" className={statusTone(it.status)}>{statusLabel(it.status)}</Badge></div>
              </button>
            );
          })
        )}
      </div>

      <ItemFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} vault={vault}
        spaceId={activeSpaceId}
        defaultFolderId={currentFolderId} folders={folders}
        onSaved={() => { load(); loadFolders(); }} />
    </div>
  );
}
